import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getColumnsWidthStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import LoanTabLayout from 'app/pages/loanApplication/LoanTabLayout';
import { documentsApi } from 'app/services/api';
import Skeleton from 'app/shared/components/common/Skeleton';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getFileDateTimeName } from 'utils/file';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import { getApplicationChecklistsAction } from 'store/loanApplications/actions';
import {
  selectApplicationChecklists,
  selectApplicationChecklistsFormattedValues,
} from 'store/loanApplications/selectors';

import Checklist from './Checklist';

const Checklists = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const theme = useTheme();
  const columnsStyles = useMemo(() => getColumnsWidthStyles(theme, { columns: 3 }), [theme]);

  const checklists = useSelector(selectApplicationChecklists);
  const checklistsResults = useSelector(selectApplicationChecklistsFormattedValues);
  const isDownloadAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.DOWNLOAD_AND_PRINT_DOCUMENT),
  );

  const isEmptyContentPlaceholder = !isLoading && _.isEmpty(checklists);

  const onDownloadChecklist = useCallback(
    async e => {
      e.stopPropagation();

      try {
        const { id } = e.currentTarget.dataset;
        const { checklist, documentId, createdDate } = checklists[id];
        const fileName = getFileDateTimeName({
          fileName: checklist.name,
          date: createdDate,
          extension: 'pdf',
        });

        const { data: pdfBlob } = await documentsApi.getContent({ documentId });

        await downloadOrShareBlob(pdfBlob, fileName);
      } catch {
        dispatch(showMessageAction({ message: 'Could not download document', variant: 'error' }));
      }
    },
    [checklists, dispatch],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getApplicationChecklistsAction({ applicationId, includePendingChanges: true })),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, setIsLoading]);

  if (isLoading) {
    return <Skeleton className="py-24" length={1} type={SKELETON_TYPES.CARD} />;
  }

  return (
    <>
      <Typography className="mb-20" variant="bold16">
        Alerts
      </Typography>

      <LoanTabLayout
        isLightBackgroundLayout
        shouldAddRadius
        content={checklistsResults.map(({ checklist, ...result }) => (
          <Checklist
            calculationResult={result}
            checklist={checklist}
            isDownloadAllowed={isDownloadAllowed}
            isLoading={isLoading}
            key={checklist.id}
            onDownloadChecklist={onDownloadChecklist}
          />
        ))}
        isEmptyContentPlaceholder={isEmptyContentPlaceholder}
        top={
          <div className={clsx(columnsStyles.wrapper, 'w-full flex')}>
            <Typography variant="medium16">Name</Typography>
            <Typography variant="medium16">Created Date</Typography>
            <Typography variant="medium16">Result</Typography>
          </div>
        }
      />
    </>
  );
};

Checklists.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Checklists);
